import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { getPersonalPlayList } from '../../../../redux/personalPlayListSlice';
import {
	useDeletePersonalPlayListMutation,
	usePersonalPlayListMutation,
	useSubmitPersonalPlayListMutation
} from '../../../../service';

export const usePersonalPlaylist = userData => {
	const dispatch = useDispatch();
	const [reqPersonalPlayList, resPersonalPlayList] = usePersonalPlayListMutation();
	const [reqPersonalDelete, resPersonalDelete] = useSubmitPersonalPlayListMutation();
	const [reqDelete, resDelete] = useDeletePersonalPlayListMutation();

	// State management
	const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [editPlaylistID, setEditPlaylistID] = useState('');
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [playlistToDelete, setPlaylistToDelete] = useState(null);
	const [showDeleteVideoModal, setShowDeleteVideoModal] = useState(false);
	const [videoToDelete, setVideoToDelete] = useState(null);

	// Get playlists from Redux store
	const playlists = useSelector(state => state.personalPlayListState.personalPlayList) || [];

	// Initial fetch of playlists
	useEffect(() => {
		if (userData.token && userData.user?.userId) {
			reqPersonalPlayList({ userId: userData.user.userId });
		}
	}, [userData.token, userData.user?.userId, showCreateModal]);

	// Update Redux store when API response changes
	useEffect(() => {
		if (resPersonalPlayList?.isSuccess && resPersonalPlayList?.data?.existingVideos) {
			dispatch(getPersonalPlayList(resPersonalPlayList?.data?.existingVideos));
		} else if (resPersonalPlayList?.isSuccess) {
			dispatch(getPersonalPlayList([]));
		}
	}, [resPersonalPlayList, dispatch]);

	// Refresh playlists handler
	const refreshPlaylists = useCallback(() => {
		if (userData.user?.userId) {
			reqPersonalPlayList({ userId: userData.user.userId });
		}
	}, [userData.user?.userId, reqPersonalPlayList]);

	// Playlist edit handler
	const handleEditPlaylist = useCallback((e, playlist) => {
		e.preventDefault();
		setEditPlaylistID(playlist?._id);
		setShowEditModal(true);
	}, []);

	// Playlist delete handler
	const handleDeletePlaylist = useCallback((e, playlist) => {
		e.preventDefault();
		setPlaylistToDelete(playlist?._id);
		setShowDeleteModal(true);
	}, []);

	// Confirm playlist deletion
	const confirmDeletePlaylist = useCallback(() => {
		if (playlistToDelete) {
			reqDelete(playlistToDelete);
		}
	}, [playlistToDelete, reqDelete]);

	// Video delete handler
	const handleDeleteVideo = useCallback((e, playlist, video) => {
		e.preventDefault();
		const updatedVideos = playlist?.videos?.filter(v => v?._id !== video?._id) || [];
		const payload = {
			...playlist,
			videos: updatedVideos
		};

		setVideoToDelete(payload);
		setShowDeleteVideoModal(true);
	}, []);

	// Confirm video deletion
	const confirmDeleteVideo = useCallback(() => {
		if (videoToDelete) {
			reqPersonalDelete({
				createdAt: videoToDelete?.createdAt,
				description: videoToDelete?.description,
				name: videoToDelete?.name,
				videos: videoToDelete?.videos,
				id: videoToDelete?._id,
				userId: userData.user?.userId
			});
		}
	}, [videoToDelete, reqPersonalDelete, userData.user?.userId]);

	// Shuffle playlist handler
	const handleShufflePlaylist = useCallback(
		(e, playlistId) => {
			e.preventDefault();

			const shuffleArray = array => {
				const newArray = [...array];
				for (let i = newArray.length - 1; i > 0; i--) {
					const j = Math.floor(Math.random() * (i + 1));
					[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
				}
				return newArray;
			};

			if (resPersonalPlayList?.data?.existingVideos?.length > 0) {
				const shuffledPlaylists = resPersonalPlayList?.data?.existingVideos?.map(obj => {
					return {
						...obj,
						videos: obj?._id === playlistId ? shuffleArray(obj?.videos) : obj?.videos
					};
				});

				if (shuffledPlaylists?.length > 0) {
					dispatch(getPersonalPlayList(shuffledPlaylists));
				}
			}
		},
		[resPersonalPlayList?.data?.existingVideos, dispatch]
	);

	// Handle successful playlist deletion
	useEffect(() => {
		if (resDelete?.isSuccess) {
			refreshPlaylists();
			setShowDeleteModal(false);
			toast.success('Playlist deleted Successfully', {
				position: 'top-center',
				duration: 3000
			});
		} else if (resDelete?.isError) {
			setShowDeleteModal(false);
			toast.error('Something went wrong', {
				position: 'top-center',
				duration: 3000
			});
		}
	}, [resDelete, refreshPlaylists]);

	// Handle successful video deletion
	useEffect(() => {
		if (resPersonalDelete?.isSuccess) {
			refreshPlaylists();
			setShowDeleteVideoModal(false);
			toast.success('Video deleted successfully', {
				position: 'top-center',
				duration: 3000
			});
		} else if (resPersonalDelete?.isError) {
			setShowDeleteVideoModal(false);
			toast.error('Something went wrong', {
				position: 'top-center',
				duration: 3000
			});
		}
	}, [resPersonalDelete, refreshPlaylists]);

	// Refresh playlists when modals close
	useEffect(() => {
		if (!showEditModal || !showCreateModal) {
			refreshPlaylists();
		}
	}, [showEditModal, showCreateModal, refreshPlaylists]);

	return {
		playlists,
		currentPlaylistIndex,
		setCurrentPlaylistIndex,
		showEditModal,
		setShowEditModal,
		showCreateModal,
		setShowCreateModal,
		editPlaylistID,
		setEditPlaylistID,
		playlistToDelete,
		setPlaylistToDelete,
		showDeleteModal,
		setShowDeleteModal,
		showDeleteVideoModal,
		setShowDeleteVideoModal,
		videoToDelete,
		setVideoToDelete,
		handleShufflePlaylist,
		handleEditPlaylist,
		handleDeletePlaylist,
		handleDeleteVideo,
		confirmDeletePlaylist,
		confirmDeleteVideo,
		refreshPlaylists
	};
};
